import {
  createContext,
  useContext,
  type FC,
  type PropsWithChildren,
} from 'react';
import { useHotkeys, type Options } from 'react-hotkeys-hook';

const InteractionLockContext = createContext(false);

export const useInteractionLock = () => useContext(InteractionLockContext);

/**
 * useHotkeys that stays off while an InteractionLock is active.
 * Call shape matches useHotkeys: options and dependencies may be either argument.
 */
export const useInteractionHotkeys: typeof useHotkeys = (
  keys,
  callback,
  optionsOrDeps,
  dependencies
) => {
  const locked = useInteractionLock();
  const depsFirst = Array.isArray(optionsOrDeps);
  const passedOptions = (depsFirst ? dependencies : optionsOrDeps) as
    Options | undefined;
  const options = {
    ...passedOptions,
    enabled: (passedOptions?.enabled ?? true) && !locked,
  };

  return useHotkeys(
    keys,
    callback,
    depsFirst ? optionsOrDeps : options,
    depsFirst ? options : dependencies
  );
};

export const InteractionLock: FC<PropsWithChildren<{ locked?: boolean }>> = ({
  locked = false,
  children,
}) => (
  <InteractionLockContext.Provider value={locked}>
    {children}
  </InteractionLockContext.Provider>
);
